import { useMemo } from "react";

import { isNumber } from "lodash";

import type { Quote } from "~/v2/api/rest/data";
import { CRYPTO_LOCALE_OPTIONS, getQuotePrecision } from "../utils";

const SMALL_FONT_DIGIT_THRESHOLD = 8;
const EXTRA_SMALL_FONT_DIGIT_THRESHOLD = 10;

const digitCount = (formatted: string) => formatted.replace(/\D/g, "").length;

interface GetCryptoPriceParams {
  quote: Quote | undefined;
  quotePrecision: number;
}

export const getCryptoPrice = ({
  quote,
  quotePrecision,
}: GetCryptoPriceParams): {
  midPrice: { value: number; display: string };
  priceChange: { value: number; percentage: string };
} => {
  const midValue =
    quote && isNumber(quote.bid) && isNumber(quote.ask)
      ? Number(((quote.bid + quote.ask) / 2).toFixed(quotePrecision))
      : 0;

  const midDisplay = midValue
    ? midValue.toLocaleString("en-US", CRYPTO_LOCALE_OPTIONS)
    : "-";

  const last = quote?.last ?? 0;
  const lastDayClosing = parseFloat(quote?.last_day_closing ?? "0");
  const priceChangeValue = last - lastDayClosing;
  const percentChange = (priceChangeValue / (lastDayClosing || 1)) * 100;

  return {
    midPrice: { value: midValue, display: midDisplay },
    priceChange: {
      value: priceChangeValue,
      percentage: `${priceChangeValue > 0 ? "+" : ""}${percentChange.toLocaleString(
        "en-US",
        { minimumFractionDigits: 0, maximumFractionDigits: 2 },
      )}%`,
    },
  };
};

export interface QuoteDisplay {
  bidDisplay: string;
  lastDisplay: string;
  askDisplay: string;
  valueSize: string;
}

interface GetQuoteDisplayParams {
  quote: Quote | undefined;
  quotePrecision: number;
}

export const getQuoteDisplay = ({
  quote,
  quotePrecision,
}: GetQuoteDisplayParams): QuoteDisplay => {
  if (!quote) {
    return {
      bidDisplay: "-",
      lastDisplay: "-",
      askDisplay: "-",
      valueSize: "text-sm",
    };
  }

  const localeOptions: Intl.NumberFormatOptions = {
    ...CRYPTO_LOCALE_OPTIONS,
    maximumFractionDigits: quotePrecision,
  };
  const format = (value: number) =>
    value.toLocaleString("en-US", localeOptions);

  const bidDisplay = format(quote.bid);
  const lastDisplay = format(quote.last);
  const askDisplay = format(quote.ask);

  const maxDigits = Math.max(
    digitCount(bidDisplay),
    digitCount(lastDisplay),
    digitCount(askDisplay),
  );
  const valueSize =
    maxDigits >= EXTRA_SMALL_FONT_DIGIT_THRESHOLD
      ? "text-[11px]"
      : maxDigits > SMALL_FONT_DIGIT_THRESHOLD
        ? "text-xs"
        : "text-sm";

  return { bidDisplay, lastDisplay, askDisplay, valueSize };
};

interface UseCryptoPriceProps {
  quote: Quote | undefined;
}

export const useCryptoPrice = ({ quote }: UseCryptoPriceProps) => {
  const quotePrecision = useMemo(
    () => getQuotePrecision(quote?.last),
    [quote?.last],
  );

  const { midPrice, priceChange } = useMemo(
    () => getCryptoPrice({ quote, quotePrecision }),
    [quote, quotePrecision],
  );

  const quoteDisplay = useMemo(
    () => getQuoteDisplay({ quote, quotePrecision }),
    [quote, quotePrecision],
  );

  return { midPrice, priceChange, quoteDisplay };
};
